import { lazy } from 'react';
import { createBrowserRouter, Navigate, Outlet, useLocation } from 'react-router-dom';
import { RedirectIfAuthenticated, RequireAuth, RequireRole } from '@/features/auth/guards';
import { AppShell } from '@/layouts/AppShell';
import { AuthLayout, FocusLayout } from '@/layouts/SimpleLayouts';
import { LandingPage, NotFoundPage } from '@/pages/LandingPage';
import { DeepLinkHandler } from './DeepLinkHandler';
import { RouteSeo } from '@/components/Seo';

// Route-level code splitting: learners never download the admin/provider portals.
const named = <T,>(loader: () => Promise<T>, name: keyof T & string) =>
  lazy(async () => ({ default: (await loader())[name] as unknown as React.ComponentType }));

const Auth = () => import('@/features/auth/AuthPages');
const LoginPage = named(Auth, 'LoginPage');
const RegisterPage = named(Auth, 'RegisterPage');
const ForgotPasswordPage = named(Auth, 'ForgotPasswordPage');
const ResetPasswordPage = named(Auth, 'ResetPasswordPage');

const PrivacyPage = named(() => import('@/pages/PrivacyPage'), 'PrivacyPage');
const TermsPage = named(() => import('@/pages/TermsPage'), 'TermsPage');
const RefundPage = named(() => import('@/pages/RefundPage'), 'RefundPage');
const AboutPage = named(() => import('@/pages/AboutPage'), 'AboutPage');
const GuidesPage = named(() => import('@/pages/GuidesPage'), 'GuidesPage');
const GuidePage = named(() => import('@/pages/GuidePage'), 'GuidePage');
const UnsubscribePage = named(() => import('@/pages/UnsubscribePage'), 'UnsubscribePage');
const TrialPage = named(() => import('@/features/trials/TrialPage'), 'TrialPage');
const DashboardPage = named(() => import('@/features/dashboard/DashboardPage'), 'DashboardPage');
const MyBanksPage = named(() => import('@/features/questionBanks/MyBanksPage'), 'MyBanksPage');
const BankDetailPage = named(() => import('@/features/questionBanks/BankDetailPage'), 'BankDetailPage');
const ModulePage = named(() => import('@/features/questionBanks/ModulePage'), 'ModulePage');
const BankEditorPage = named(() => import('@/features/questionBanks/BankEditorPage'), 'BankEditorPage');
const PracticeSetupPage = named(() => import('@/features/practice/PracticeSetupPage'), 'PracticeSetupPage');
const Practice = () => import('@/features/practice/PracticeSessionPage');
const PracticeSessionPage = named(Practice, 'PracticeSessionPage');
const PreviewPracticePage = named(Practice, 'PreviewPracticePage');
const ExamSetupPage = named(() => import('@/features/exams/ExamSetupPage'), 'ExamSetupPage');
const ExamSessionPage = named(() => import('@/features/exams/ExamSessionPage'), 'ExamSessionPage');
const ResultsPage = named(() => import('@/features/results/ResultsPage'), 'ResultsPage');
const ProgressPage = named(() => import('@/features/analytics/ProgressPage'), 'ProgressPage');
const Store = () => import('@/features/store/StorePages');
const StoreHomePage = named(Store, 'StoreHomePage');
const StoreSearchPage = named(Store, 'StoreSearchPage');
const ProductPage = named(() => import('@/features/store/ProductPage'), 'ProductPage');
const ProviderPage = named(() => import('@/features/store/ProviderPage'), 'ProviderPage');
const Checkout = () => import('@/features/purchases/CheckoutPages');
const CheckoutSuccessPage = named(Checkout, 'CheckoutSuccessPage');
const DevCheckoutPage = named(Checkout, 'DevCheckoutPage');
const PurchasesPage = named(() => import('@/features/purchases/PurchasesPage'), 'PurchasesPage');
const ProfilePage = named(() => import('@/features/profile/ProfilePage'), 'ProfilePage');
const SupportPage = named(() => import('@/features/support/SupportPage'), 'SupportPage');

const BecomeProviderPage = named(() => import('@/features/contentProviders/BecomeProviderPage'), 'BecomeProviderPage');
const ProviderDashboardPage = named(() => import('@/features/contentProviders/ProviderDashboardPage'), 'ProviderDashboardPage');
const ProviderBanksPage = named(() => import('@/features/contentProviders/ProviderBanksPage'), 'ProviderBanksPage');
const ProviderProductsPage = named(() => import('@/features/products/ProviderProductsPage'), 'ProviderProductsPage');
const ProductEditor = () => import('@/features/products/ProductEditorPage');
const NewProductPage = named(ProductEditor, 'NewProductPage');
const ProductEditorPage = named(ProductEditor, 'ProductEditorPage');
const NewBundlePage = named(() => import('@/features/products/BundleEditor'), 'NewBundlePage');
const ProviderFinance = () => import('@/features/contentProviders/ProviderFinancePages');
const ProviderSalesPage = named(ProviderFinance, 'ProviderSalesPage');
const ProviderAnalyticsPage = named(ProviderFinance, 'ProviderAnalyticsPage');
const ProviderPayoutsPage = named(ProviderFinance, 'ProviderPayoutsPage');
const ProviderProfilePage = named(ProviderFinance, 'ProviderProfilePage');

const AdminOverviewPage = named(() => import('@/features/admin/AdminOverviewPage'), 'AdminOverviewPage');
const AdminEngagementPage = named(() => import('@/features/admin/AdminEngagementPage'), 'AdminEngagementPage');
const AdminPeople = () => import('@/features/admin/AdminPeoplePages');
const AdminUsersPage = named(AdminPeople, 'AdminUsersPage');
const AdminProvidersPage = named(AdminPeople, 'AdminProvidersPage');
const AdminContent = () => import('@/features/admin/AdminContentPages');
const AdminProductsPage = named(AdminContent, 'AdminProductsPage');
const AdminBanksPage = named(AdminContent, 'AdminBanksPage');
const AdminReviewsPage = named(AdminContent, 'AdminReviewsPage');
const AdminFinance = () => import('@/features/admin/AdminFinancePages');
const AdminOrdersPage = named(AdminFinance, 'AdminOrdersPage');
const AdminPayoutsPage = named(AdminFinance, 'AdminPayoutsPage');
const AdminSettings = () => import('@/features/admin/AdminSettingsPages');
const AdminSettingsPage = named(AdminSettings, 'AdminSettingsPage');
const AdminCataloguePage = named(AdminSettings, 'AdminCataloguePage');

const ProviderAiPage = named(() => import('@/features/ai/ProviderAiPage'), 'ProviderAiPage');
const PlusPage = named(() => import('@/features/ai/PlusPage'), 'PlusPage');
const StudyPlanPage = named(() => import('@/features/plus/StudyPlanPage'), 'StudyPlanPage');
const AdminAiPage = named(() => import('@/features/ai/AdminAiPage'), 'AdminAiPage');
const AdminGiftsPage = named(() => import('@/features/admin/AdminGiftsPage'), 'AdminGiftsPage');
const AdminCredentialsPage = named(() => import('@/features/admin/AdminCredentialsPage'), 'AdminCredentialsPage');
const ProviderCredentialsPage = named(() => import('@/features/contentProviders/ProviderCredentialsPage'), 'ProviderCredentialsPage');

const authed = (el: React.ReactNode) => <RequireAuth>{el}</RequireAuth>;

/** Redirect a renamed page, keeping its query string. */
function MovedTo({ pathname }: { pathname: string }) {
  const { search } = useLocation();
  return <Navigate to={{ pathname, search }} replace />;
}

export const router = createBrowserRouter([
  {
    element: (
      <>
        <DeepLinkHandler />
        <RouteSeo />
        <Outlet />
      </>
    ),
    children: [
      { path: '/', element: <LandingPage /> },

      {
        element: (
          <RedirectIfAuthenticated>
            <AuthLayout />
          </RedirectIfAuthenticated>
        ),
        children: [
          { path: '/login', element: <LoginPage /> },
          { path: '/register', element: <RegisterPage /> },
          { path: '/forgot-password', element: <ForgotPasswordPage /> },
        ],
      },
      { element: <AuthLayout />, children: [{ path: '/reset-password', element: <ResetPasswordPage /> }] },

      // Full-screen study experiences
      {
        element: <FocusLayout />,
        children: [
          { path: '/practice/session', element: authed(<PracticeSessionPage />) },
          { path: '/preview/:slug', element: <PreviewPracticePage /> },
          { path: '/exam/:id', element: authed(<ExamSessionPage />) },
          { path: '/checkout/dev', element: authed(<DevCheckoutPage />) },
        ],
      },

      {
        element: <AppShell />,
        children: [
          // Public Store (SEO-friendly URLs)
          { path: '/store', element: <StoreHomePage /> },
          { path: '/store/search', element: <StoreSearchPage /> },
          { path: '/store/providers/:slug', element: <ProviderPage /> },
          { path: '/store/:slug', element: <ProductPage /> },
          { path: '/support', element: <SupportPage /> },
          { path: '/privacy', element: <PrivacyPage /> },
          { path: '/terms', element: <TermsPage /> },
          { path: '/refunds', element: <RefundPage /> },
          { path: '/about', element: <AboutPage /> },
          { path: '/guides', element: <GuidesPage /> },
          { path: '/guides/:slug', element: <GuidePage /> },
          // The unsubscribe link in new product emails (works signed out)
          { path: '/unsubscribe', element: <UnsubscribePage /> },
          // Free trial from a provider's QR code (works signed out)
          { path: '/t/:code', element: <TrialPage /> },

          // Learner
          { path: '/dashboard', element: authed(<DashboardPage />) },
          { path: '/question-banks', element: authed(<MyBanksPage />) },
          { path: '/question-banks/:id', element: authed(<BankDetailPage />) },
          { path: '/question-banks/:id/edit', element: authed(<BankEditorPage />) },
          { path: '/question-banks/:id/modules/:moduleId', element: authed(<ModulePage />) },
          { path: '/practice', element: authed(<PracticeSetupPage />) },
          { path: '/exam', element: authed(<ExamSetupPage />) },
          { path: '/results/:id', element: authed(<ResultsPage />) },
          { path: '/progress', element: authed(<ProgressPage />) },
          { path: '/purchases', element: authed(<PurchasesPage />) },
          { path: '/checkout/success', element: authed(<CheckoutSuccessPage />) },
          { path: '/profile', element: authed(<ProfilePage />) },
          { path: '/plus', element: authed(<PlusPage />) },
          { path: '/study-plan', element: authed(<StudyPlanPage />) },

          // Content Provider Portal
          {
            path: '/provider',
            element: (
              <RequireRole role="content_provider">
                <Outlet />
              </RequireRole>
            ),
            children: [
              { index: true, element: <ProviderDashboardPage /> },
              { path: 'products', element: <ProviderProductsPage /> },
              { path: 'products/new', element: <NewProductPage /> },
              { path: 'products/new-bundle', element: <NewBundlePage /> },
              { path: 'products/:id', element: <ProductEditorPage /> },
              { path: 'question-banks', element: <ProviderBanksPage /> },
              // Same editor as /question-banks/:id/edit, but inside the provider portal so its menu stays.
              { path: 'question-banks/:id/edit', element: <BankEditorPage /> },
              { path: 'sales', element: <ProviderSalesPage /> },
              { path: 'analytics', element: <ProviderAnalyticsPage /> },
              { path: 'payouts', element: <ProviderPayoutsPage /> },
              { path: 'profile', element: <ProviderProfilePage /> },
              { path: 'credentials', element: <ProviderCredentialsPage /> },
              { path: 'setup', element: <BecomeProviderPage /> },
              { path: 'workshop', element: <ProviderAiPage /> },
              // Old address (and card top-ups started before the rename) keep their ?topup= status.
              { path: 'ai', element: <MovedTo pathname="/provider/workshop" /> },
            ],
          },

          // Admin Portal (every action is also authorised in the database)
          {
            path: '/admin',
            element: (
              <RequireRole role="admin">
                <Outlet />
              </RequireRole>
            ),
            children: [
              { index: true, element: <AdminOverviewPage /> },
              { path: 'engagement', element: <AdminEngagementPage /> },
              { path: 'users', element: <AdminUsersPage /> },
              { path: 'providers', element: <AdminProvidersPage /> },
              { path: 'products', element: <AdminProductsPage /> },
              { path: 'question-banks', element: <AdminBanksPage /> },
              { path: 'orders', element: <AdminOrdersPage /> },
              { path: 'payments', element: <Navigate to="/admin/orders?tab=transactions" replace /> },
              { path: 'payouts', element: <AdminPayoutsPage /> },
              { path: 'reviews', element: <AdminReviewsPage /> },
              { path: 'catalogue', element: <AdminCataloguePage /> },
              { path: 'settings', element: <AdminSettingsPage /> },
              { path: 'ai', element: <AdminAiPage /> },
              { path: 'gifts', element: <AdminGiftsPage /> },
              { path: 'credentials', element: <AdminCredentialsPage /> },
            ],
          },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);
